import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "./useAuth";
import React from "react";

const TRIAL_CREDITS_KEY = "vml_trial_credits";
const TRIAL_INITIAL = 5;

interface Credits {
  balance: number;
  tier: "trial" | "free" | "starter" | "pro";
  monthly_allowance: number;
  rollover_balance: number;
}

function getTrialBalance(): number {
  const stored = localStorage.getItem(TRIAL_CREDITS_KEY);
  if (stored === null) {
    localStorage.setItem(TRIAL_CREDITS_KEY, String(TRIAL_INITIAL));
    return TRIAL_INITIAL;
  }
  const val = Math.max(0, parseInt(stored, 10) || 0);
  if (val > TRIAL_INITIAL) {
    localStorage.setItem(TRIAL_CREDITS_KEY, String(TRIAL_INITIAL));
    return TRIAL_INITIAL;
  }
  return val;
}

function setTrialBalance(balance: number) {
  localStorage.setItem(TRIAL_CREDITS_KEY, String(Math.max(0, balance)));
}

interface CreditsContextValue {
  credits: Credits | null;
  loading: boolean;
  deductCredits: (cost: number) => boolean;
  fetchCredits: () => Promise<void>;
  setBalance: (newBalance: number) => void;
}

const CreditsContext = createContext<CreditsContextValue | null>(null);

export function CreditsProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const [credits, setCredits] = useState<Credits | null>(null);
  const [loading, setLoading] = useState(true);

  const fetchCredits = useCallback(async () => {
    if (!user?.id) {
      setCredits({
        balance: getTrialBalance(),
        tier: "trial",
        monthly_allowance: 0,
        rollover_balance: 0,
      });
      setLoading(false);
      return;
    }

    setLoading(true);

    const { data, error } = await supabase
      .from("credits")
      .select("balance, tier, monthly_allowance, rollover_balance")
      .eq("user_id", user.id)
      .single();

    if (!error && data) {
      setCredits(data as Credits);
    }

    setLoading(false);
  }, [user?.id]);

  useEffect(() => {
    void fetchCredits();
  }, [fetchCredits]);

  const deductCredits = useCallback((cost: number): boolean => {
    if (!credits || credits.balance < cost) return false;

    const newBalance = credits.balance - cost;

    if (!user) {
      setTrialBalance(newBalance);
    }

    setCredits((prev) => (prev ? { ...prev, balance: newBalance } : prev));
    return true;
  }, [credits, user]);

  const setBalance = useCallback((newBalance: number) => {
    if (!user) {
      setTrialBalance(newBalance);
    }
    setCredits((prev) => (prev ? { ...prev, balance: newBalance } : prev));
  }, [user]);

  const value: CreditsContextValue = { credits, loading, deductCredits, fetchCredits, setBalance };

  return React.createElement(CreditsContext.Provider, { value }, children);
}

export function useCredits(): CreditsContextValue {
  const ctx = useContext(CreditsContext);
  if (!ctx) {
    throw new Error("useCredits must be used within a CreditsProvider");
  }
  return ctx;
}
